import React, { useState, useEffect } from 'react';
import { Card, Button, Upload, message, Typography, Space, Table, Spin, Popconfirm, Divider, Tag, List, Badge, Empty, Row, Col } from 'antd';
import { UploadOutlined, PrinterOutlined, FileExcelOutlined, DeleteOutlined, ClearOutlined, HistoryOutlined, RightOutlined } from '@ant-design/icons';
import { getCheckPrintRecords, uploadCheckPrintExcel, deleteCheckRecord, clearCheckRecords, exportChecksPdf, downloadCheckSample } from '../api';
import moment from 'moment';

const { Title, Text, Paragraph } = Typography;

const CheckPrinting = () => {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(false);
    const [data, setData] = useState([]);
    const [batches, setBatches] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const fetchData = async (uploadDate = null) => {
        setPageLoading(true);
        try {
            const params = uploadDate ? { upload_date: uploadDate } : {};
            const res = await getCheckPrintRecords(params);
            if (res.success) {
                setData(res.data);
                setBatches(res.batches || []);
                if (!uploadDate && res.batches && res.batches.length > 0) {
                    setSelectedDate(res.batches[0].upload_date);
                }
            }
        } catch (error) {
            message.error('Failed to fetch check records.');
        } finally {
            setPageLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleBatchClick = (date) => {
        setSelectedDate(date);
        fetchData(date);
    };

    const handleUpload = async () => {
        if (!file) {
            message.error('Please select an Excel file first.');
            return;
        }

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await uploadCheckPrintExcel(formData);
            
            if (response.success) {
                setFile(null);
                message.success(response.message || 'Records imported successfully!');
                fetchData(); // This will refresh and select the newest batch
            } else {
                message.error(response.message || 'Failed to process Excel.');
            }
        } catch (error) {
            message.error('An error occurred while uploading.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteCheckRecord(id);
            message.success('Record deleted.');
            fetchData(selectedDate);
        } catch (error) {
            message.error('Failed to delete record.');
        }
    };

    const handleClearAll = async () => {
        try {
            await clearCheckRecords();
            message.success('All records cleared.');
            setSelectedDate(null);
            setData([]);
            setBatches([]);
        } catch (error) {
            message.error('Failed to clear records.');
        }
    };

    const handlePrint = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning('Please select at least one record to print.');
            return;
        }

        setLoading(true);
        try {
            const blob = await exportChecksPdf(selectedRowKeys);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Checks_${moment().format('YYYYMMDD_HHmm')}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            message.success('Check PDF generated successfully!');
        } catch (error) {
            message.error('Failed to generate PDF.');
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Supplier Name',
            dataIndex: 'supplier_name',
            key: 'supplier_name',
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (amt) => amt ? <Tag color="blue" style={{ fontSize: '12px', fontWeight: 'bold' }}>AED {parseFloat(amt).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Tag> : '-'
        },
        {
            title: 'Date',
            dataIndex: 'check_date',
            key: 'check_date',
        },
        {
            title: 'Words',
            dataIndex: 'amount_words',
            key: 'amount_words',
            ellipsis: true,
            width: 250
        },
        {
            title: 'Action',
            key: 'action',
            width: 80,
            render: (_, record) => (
                <Popconfirm title="Delete this record?" onConfirm={() => handleDelete(record.id)}>
                    <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
            )
        }
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
    };

    return (
        <div style={{ padding: '0px', maxWidth: '1400px', margin: '0 auto' }}>
            {/* Header Section */}
            <Card style={{ marginBottom: '24px', borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <div style={{ background: 'linear-gradient(135deg, #003a8c 0%, #001529 100%)', color: 'white', padding: '24px' }}>
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Title level={3} style={{ color: 'white', margin: 0 }}>
                                <PrinterOutlined style={{ marginRight: 12 }} />
                                Check Printing Utility
                            </Title>
                            <Paragraph style={{ color: 'rgba(255,255,255,0.7)', marginTop: 4, marginBottom: 0 }}>
                                Manage your payment batches and print professional bank checks.
                            </Paragraph>
                        </Col>
                        <Col>
                           <Space>
                                <Upload
                                    beforeUpload={(file) => {
                                        setFile(file);
                                        return false;
                                    }}
                                    maxCount={1}
                                    accept=".xlsx,.xls,.csv"
                                    showUploadList={false}
                                >
                                    <Button icon={<FileExcelOutlined />} style={{ borderRadius: '6px' }}>
                                        {file ? file.name : 'Select Excel File'}
                                    </Button>
                                </Upload>
                                <Button 
                                    type="primary" 
                                    onClick={handleUpload} 
                                    loading={loading}
                                    disabled={!file}
                                    style={{ borderRadius: '6px' }}
                                >
                                    Import Batch
                                </Button>
                                <Button 
                                    type="link" 
                                    style={{ color: '#aaa' }}
                                    onClick={async () => {
                                        const blob = await downloadCheckSample();
                                        const url = window.URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = 'Check_Import_Template.csv';
                                        a.click();
                                    }}
                                >
                                    Download Code Sample
                                </Button>
                           </Space>
                        </Col>
                    </Row>
                </div>
            </Card>

            <Row gutter={24}>
                {/* Left Sidebar: Batch History */}
                <Col span={6}>
                    <Card 
                        title={<span><HistoryOutlined /> Upload History</span>}
                        style={{ borderRadius: '12px', height: '100%', minHeight: '600px' }}
                        extra={
                            <Popconfirm title="Delete ALL records?" onConfirm={handleClearAll}>
                                <Button type="text" danger icon={<ClearOutlined />} size="small">Clear All</Button>
                            </Popconfirm>
                        }
                    >
                        {batches.length > 0 ? (
                            <List
                                dataSource={batches}
                                renderItem={(item) => (
                                    <List.Item 
                                        onClick={() => handleBatchClick(item.upload_date)}
                                        style={{ 
                                            cursor: 'pointer', 
                                            padding: '12px',
                                            borderRadius: '8px',
                                            marginBottom: '8px',
                                            background: selectedDate === item.upload_date ? '#e6f7ff' : 'transparent',
                                            border: selectedDate === item.upload_date ? '1px solid #91d5ff' : '1px solid transparent',
                                            transition: 'all 0.3s'
                                        }}
                                    >
                                        <div style={{ width: '100%' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Text strong style={{ color: selectedDate === item.upload_date ? '#1890ff' : '#333' }}>
                                                    {moment(item.upload_date).format('DD MMM YYYY')}
                                                </Text>
                                                <Badge count={item.count} overflowCount={999} style={{ backgroundColor: '#52c41a' }} />
                                            </div>
                                            <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                                                Check Batch
                                            </div>
                                        </div>
                                    </List.Item>
                                )}
                            />
                        ) : (
                            <Empty description="No history yet" />
                        )}
                    </Card>
                </Col>

                {/* Right Content: Batch Details */}
                <Col span={18}>
                    <Card 
                        style={{ borderRadius: '12px' }}
                        title={
                            selectedDate ? (
                                <Space>
                                    <Text type="secondary">Batch Detail:</Text>
                                    <Text strong>{moment(selectedDate).format('DD MMMM YYYY')}</Text>
                                </Space>
                            ) : "Records"
                        }
                        extra={
                            <Button 
                                type="primary" 
                                icon={<PrinterOutlined />} 
                                onClick={handlePrint}
                                loading={loading}
                                disabled={selectedRowKeys.length === 0}
                                style={{ background: '#52c41a', borderColor: '#52c41a' }}
                            >
                                Print Selected ({selectedRowKeys.length})
                            </Button>
                        }
                    >
                        <Table 
                            rowSelection={rowSelection}
                            columns={columns} 
                            dataSource={data} 
                            rowKey="id"
                            loading={pageLoading}
                            pagination={{ pageSize: 12 }}
                            size="small"
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default CheckPrinting;

